// Navigation der Immobilien-Sidebar (README §5.6) – reine Daten und Funktionen.

import { istWohnend } from '../dashboard/leerstand';
import { aktuellerStand, gesamtmiete, leseStand } from '../mieter/mietstaende';
import { verwaltungsartAusObjekttyp } from '../objekte/verwaltungsart';

/** Hauptnavigation im Listenmodus – Reihenfolge laut Prototyp. id = Modul-id der App. */
export const HAUPT_NAV = [
  { id: 'dashboard', label: 'Dashboard', path: '/admin/dashboard' },
  { id: 'objekte', label: 'Immobilien', path: '/admin/objekte' },
  { id: 'mieter', label: 'Mieter', path: '/admin/mieter' },
  { id: 'eigentuemer', label: 'Eigentümer', path: '/admin/eigentuemer' },
  { id: 'dienstleister', label: 'Dienstleister', path: '/admin/dienstleister' },
  // Sonstige Kontakte (Handoff Sonstige; Entscheidung 19.10.2026: in der Hauptnavigation, Name „Kontakte“)
  { id: 'kontakte', label: 'Kontakte', path: '/admin/kontakte' },
  { id: 'rechnungen', label: 'Rechnungen', path: '/admin/rechnungen' },
  { id: 'dokumente', label: 'Dokumente', path: '/admin/dokumente' },
  { id: 'tickets', label: 'Tickets', path: '/admin/tickets' },
  { id: 'kalender', label: 'Kalender', path: '/admin/kalender' },
  { id: 'vertraege', label: 'Verträge', path: '/admin/vertraege' },
  { id: 'zaehler', label: 'Zähler', path: '/admin/zaehler' },
  { id: 'emails', label: 'E-Mails', path: '/admin/emails' },
  { id: 'team', label: 'Team', path: '/admin/team' },
] as const;

export type HauptNavId = typeof HAUPT_NAV[number]['id'];

/** Icon-Leiste unten im Objektmodus (nach dem Hell/Dunkel-Schalter). */
export const DOCK_NAV: readonly HauptNavId[] = ['dashboard', 'objekte', 'mieter', 'rechnungen', 'kalender'];

export interface WeitererEintrag { id: string; label: string; path: string; /** weitere Bereiche, für die dieser Eintrag steht (Reiter der Seite) */ auch?: string[] }

/**
 * Bereiche, die als Ordner im E-Mail-Bereich leben und deshalb nicht mehr als eigener Menüpunkt erscheinen
 * (Entscheidung E-Mails 2: Serienmail → /admin/emails?ordner=serienmail). Modul und Rechte bleiben unverändert.
 */
export const IM_EMAIL_BEREICH: ReadonlySet<string> = new Set(['serienmail']);

/** Gruppen der Seitenleiste (Patrick 01.11.2026): Überbereiche zum Aufklappen. Dashboard bleibt oben allein. */
export const NAV_GRUPPEN: readonly { id: string; label: string; ids: readonly string[] }[] = [
  { id: 'dashboard', label: 'Dashboard', ids: ['dashboard'] },
  { id: 'immobilien', label: 'Immobilien', ids: ['objekte', 'zaehler'] },
  { id: 'kontakte', label: 'Kontakte', ids: ['mieter', 'eigentuemer', 'dienstleister', 'kontakte'] },
  { id: 'vermietung', label: 'Vermietung', ids: ['vertraege', 'mieterselbstauskunft', 'uebergabeprotokolle'] },
  { id: 'kommunikation', label: 'Kommunikation', ids: ['emails', 'schreiben', 'tickets', 'kalender'] },
  { id: 'finanzen', label: 'Dokumente & Finanzen', ids: ['dokumente', 'rechnungen'] },
  { id: 'personal', label: 'Personal', ids: ['zeiterfassung', 'team'] },
  { id: 'system', label: 'System', ids: ['super-admin'] },
];

/** Bereiche, die als Reiter einer Seite erreichbar sind → ein Eintrag (der erste erlaubte steht für alle). */
const ZUSAMMEN: Record<string, readonly string[]> = { zeiterfassung: ['zeiterfassung', 'abwesenheiten', 'personalakte', 'monatsabschluss'] };

/** Bezeichnungen, die in der Gruppe eindeutiger sein müssen */
const GRUPPEN_LABEL: Record<string, string> = { kontakte: 'Sonstige Kontakte', objekte: 'Immobilien' };

export interface NavGruppe { id: string; label: string; eintraege: WeitererEintrag[]; /** nur ein Eintrag → direkter Link statt Aufklappen */ einzeln: boolean }

/** Sichtbare Bereiche (Rechte schon geprüft) → Gruppen; leere fallen weg, Unbekanntes landet in „Weitere“. */
export function baueNavGruppen(sichtbar: readonly WeitererEintrag[]): NavGruppe[] {
  const nachId = new Map<string, WeitererEintrag>(sichtbar.filter((e) => !IM_EMAIL_BEREICH.has(e.id)).map((e) => [e.id, e]));
  const vergeben = new Set<string>();
  // Reiter-Gruppen: der erste erlaubte Bereich steht unter dem Schlüssel für alle (Rechte bleiben je Bereich)
  for (const [schluessel, ids] of Object.entries(ZUSAMMEN)) {
    const da = ids.filter((id) => nachId.has(id));
    if (!da.length) continue;
    const vertreter = nachId.get(da[0])!;
    for (const id of da) { nachId.delete(id); vergeben.add(id); }
    nachId.set(schluessel, { ...vertreter, ...(da.length > 1 ? { auch: da.slice(1) } : {}) });
  }
  const aus: NavGruppe[] = [];
  for (const g of NAV_GRUPPEN) {
    const eintraege = g.ids.flatMap((id) => {
      const e = nachId.get(id);
      if (!e) return [];
      vergeben.add(id);
      return [{ ...e, label: GRUPPEN_LABEL[e.id] ?? e.label }];
    });
    if (eintraege.length) aus.push({ id: g.id, label: g.label, eintraege, einzeln: eintraege.length === 1 });
  }
  const rest = [...nachId.entries()].filter(([k]) => !vergeben.has(k)).map(([, e]) => e);
  if (rest.length) aus.push({ id: 'weitere', label: 'Weitere', eintraege: rest, einzeln: rest.length === 1 });
  return aus;
}

export const istAktiv = (e: WeitererEintrag, bereichId: string) => e.id === bereichId || !!e.auch?.includes(bereichId);
export const gruppeVon = (gruppen: readonly NavGruppe[], bereichId: string) => gruppen.find((g) => g.eintraege.some((e) => istAktiv(e, bereichId)))?.id ?? null;

/** Wo im Immobilienbereich sind wir? */
export type ObjektBereich = 'uebersicht' | 'einheiten' | 'abrechnung';

export interface ObjektOrt {
  objektId: string;
  bereich: ObjektBereich;
  einheitId: string | null;
}

/**
 * Liest Objekt, Bereich und Einheit aus dem Pfad.
 * /admin/objekte → null (Listenmodus)
 * /admin/objekte/:id → Übersicht
 * /admin/objekte/:id/einheiten[/:einheitId] → Einheiten
 * /admin/objekte/:id/einheit/:einheitId (alt) → Einheiten
 * /admin/objekte/:id/abrechnung → Abrechnung
 */
export function objektOrtAusPfad(pfad: string): ObjektOrt | null {
  const teile = pfad.split('/').filter(Boolean);
  if (teile[0] !== 'admin' || teile[1] !== 'objekte') return null;
  const objektId = teile[2];
  if (!objektId) return null;
  const abschnitt = teile[3];
  const roheEinheit = teile[4];
  const einheitId = roheEinheit ? decodeURIComponent(roheEinheit) : null;
  if (abschnitt === 'einheiten' || abschnitt === 'einheit') return { objektId, bereich: 'einheiten', einheitId };
  if (abschnitt === 'abrechnung') return { objektId, bereich: 'abrechnung', einheitId: null };
  return { objektId, bereich: 'uebersicht', einheitId: null };
}

export type VerwaltungsartKurz = 'MV' | 'WEG' | 'SEV' | 'GV';

/** objekte.verwaltungsart hat Vorrang; sonst aus dem alten Freitext objekttyp ableiten. */
export function verwaltungsartVonObjekt(o: { verwaltungsart?: string | null; objekttyp?: string | null }): VerwaltungsartKurz | null {
  const v = (o.verwaltungsart ?? '').trim().toUpperCase();
  if (v === 'MV' || v === 'WEG' || v === 'SEV' || v === 'GV') return v;
  return verwaltungsartAusObjekttyp(o.objekttyp)?.code ?? null;
}

// ── Einheiten für die aufklappbare Liste ─────────────────────────────

export interface EinheitRoh { id: string; bezeichnung: string | null; haus_id: string | null }
export interface MieterRoh {
  einheit_id: string | null;
  status: string | null;
  kaltmiete: number | string | null;
  nebenkosten_vorauszahlung: number | string | null;
  heizkosten: number | string | null;
  weitere_kosten: number | string | null;
  miet_historie: unknown;
}
export interface HausRoh { id: string; bezeichnung: string | null }

export interface EinheitNavEintrag {
  id: string;
  bezeichnung: string;
  hausId: string | null;
  vermietet: boolean;
  /** Monatliche Gesamtmiete der wohnenden Mieter (0 bei Leerstand). */
  miete: number;
}

const zahl = (v: unknown): number => {
  const n = typeof v === 'number' ? v : typeof v === 'string' ? Number(v.replace(',', '.')) : NaN;
  return Number.isFinite(n) ? n : 0;
};

/** Aktuelle Gesamtmiete eines Mieters: Stand aus miet_historie, sonst die Spiegelfelder. */
export function aktuelleGesamtmiete(m: MieterRoh, heute: string): number {
  const historie = Array.isArray(m.miet_historie) ? m.miet_historie.map((roh, i) => leseStand(roh, i)) : [];
  const stand = aktuellerStand(historie, heute);
  if (stand) return gesamtmiete(stand);
  return zahl(m.kaltmiete) + zahl(m.nebenkosten_vorauszahlung) + zahl(m.heizkosten) + zahl(m.weitere_kosten);
}

const sortierSchluessel = new Intl.Collator('de', { numeric: true, sensitivity: 'base' });

export function baueEinheitenNav(einheiten: readonly EinheitRoh[], mieter: readonly MieterRoh[], heute: string): EinheitNavEintrag[] {
  const proEinheit = new Map<string, MieterRoh[]>();
  for (const m of mieter) {
    if (!m.einheit_id || !istWohnend(m.status)) continue;
    const liste = proEinheit.get(m.einheit_id) ?? [];
    liste.push(m);
    proEinheit.set(m.einheit_id, liste);
  }
  return einheiten
    .map(e => {
      const wohnend = proEinheit.get(e.id) ?? [];
      return {
        id: e.id,
        bezeichnung: (e.bezeichnung ?? '').trim() || 'Ohne Bezeichnung',
        hausId: e.haus_id,
        vermietet: wohnend.length > 0,
        miete: Math.round(wohnend.reduce((s, m) => s + aktuelleGesamtmiete(m, heute), 0) * 100) / 100,
      };
    })
    .sort((a, b) => sortierSchluessel.compare(a.bezeichnung, b.bezeichnung));
}

export interface HausNavEintrag { id: string; bezeichnung: string; anzahl: number }

export function baueHaeuserNav(haeuser: readonly HausRoh[], einheiten: readonly EinheitNavEintrag[]): HausNavEintrag[] {
  return haeuser
    .map(h => ({ id: h.id, bezeichnung: (h.bezeichnung ?? '').trim() || 'Haus', anzahl: einheiten.filter(e => e.hausId === h.id).length }))
    .sort((a, b) => sortierSchluessel.compare(a.bezeichnung, b.bezeichnung));
}

const NEUES_DESIGN = ['/admin/objekte', '/admin/kalender', '/admin/mieter', '/admin/eigentuemer', '/admin/dienstleister', '/admin/dokumente', '/admin/schreiben', '/admin/emails', '/admin/zaehler', '/admin/tickets', '/admin/mieterselbstauskunft', '/admin/uebergabeprotokolle', '/admin/vertraege', '/admin/rechnungen', '/admin/zeiterfassung', '/admin/abwesenheiten', '/admin/personalakte', '/admin/monatsabschluss', '/admin/team', '/admin/super-admin', '/admin/profile', '/admin/dashboard', '/admin/kontakte/kontakt'];

/** Läuft der Pfad im neuen Design (eigene Sidebar)? Immobilien, Kalender (E9), Mieter (M1), Eigentümer (E1) und Dienstleister (E1). */
/** Nur genau diese Pfade (nicht ihre Unterseiten): Sonstige Kontakte – /admin/kontakte/mieter/… bleibt im alten Layout. */
const NEUES_DESIGN_EXAKT = ['/admin/kontakte'];

export function istImmobilienBereich(pfad: string): boolean {
  return NEUES_DESIGN.some(basis => pfad === basis || pfad.startsWith(`${basis}/`)) || NEUES_DESIGN_EXAKT.includes(pfad.replace(/\/$/, ''));
}

/** Mieter-ID im Mietermodus (/admin/mieter/:id…), sonst null. */
export function mieterIdAusPfad(pfad: string): string | null {
  const treffer = /^\/admin\/mieter\/([^/?#]+)/.exec(pfad);
  if (!treffer?.[1]) return null;
  try { return decodeURIComponent(treffer[1]); } catch { return treffer[1]; }
}

/** Dienstleister-ID im Dienstleistermodus (/admin/dienstleister/:id…), sonst null. */
export function dienstleisterIdAusPfad(pfad: string): string | null {
  const treffer = /^\/admin\/dienstleister\/([^/?#]+)/.exec(pfad);
  if (!treffer?.[1]) return null;
  try { return decodeURIComponent(treffer[1]); } catch { return treffer[1]; }
}

/** Kontakt-ID im Kontaktmodus (Sonstige: /admin/kontakte/kontakt/:id…), sonst null. */
export function sonstigerKontaktIdAusPfad(pfad: string): string | null {
  const treffer = /^\/admin\/kontakte\/kontakt\/([^/?#]+)/.exec(pfad);
  if (!treffer?.[1]) return null;
  try { return decodeURIComponent(treffer[1]); } catch { return treffer[1]; }
}

/** Eigentümer-ID im Eigentümermodus (/admin/eigentuemer/:id…), sonst null. */
export function eigentuemerIdAusPfad(pfad: string): string | null {
  const treffer = /^\/admin\/eigentuemer\/([^/?#]+)/.exec(pfad);
  if (!treffer?.[1]) return null;
  try { return decodeURIComponent(treffer[1]); } catch { return treffer[1]; }
}
